import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { 
  Calendar, Settings, Users, BarChart3, 
  Plus, Edit, Trash2, Eye, Filter, Search,
  Heart, UserPlus, TrendingUp, Clock,
  Folder, MessageCircle, Star, Save, X,
  RefreshCw, Activity, Award, BookOpen,
  Users2, MapPin, CalendarDays, CheckCircle, Download
} from 'lucide-react';
import { 
  getEventsOverview,
  getEventAnalytics,
  createQuickStory,
  getUsersForStoryCreation,
  getArtworksForStoryCreation,
  updateStoryFeatured,
  deleteStory,
  getGalleries,
  createEvent
} from '../utils/eventsAdminApi';
import { uploadToCloudinary } from '../utils/cloudinaryUpload';
import '../styles/EventsAdmin.css';

const EventsAdmin = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [events, setEvents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showNewEventModal, setShowNewEventModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [users, setUsers] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [creating, setCreating] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [scrapingArtforum, setScrapingArtforum] = useState(false);
  const [scraperSource, setScraperSource] = useState('cgn'); // 'cgn' or 'artforum'
  const [artforumCountry, setArtforumCountry] = useState('United States');
  const [artforumCity, setArtforumCity] = useState('Chicago');
  const [artforumDistrict, setArtforumDistrict] = useState('all');
  const [artforumCategory, setArtforumCategory] = useState('galleries');

  // Artforum cities organized by country
  const ARTFORUM_CITIES_BY_COUNTRY = {
    'United States': [
      'Ann Arbor', 'Austin', 'Beacon', 'Boston', 'Chicago', 'Claremont', 'Cleveland',
      'Clinton', 'Cochranville', 'Cold Spring', 'College Station', 'Columbus', 'Concord',
      'Corpus Christi', 'Dallas', 'Davis', 'Denver', 'Detroit', 'Durham', 'East Hampton',
      'Houston', 'Los Angeles', 'Miami', 'Minneapolis', 'New Haven', 'New York',
      'Philadelphia', 'Portland, OR', 'San Francisco', 'Seattle'
    ],
    'Canada': ['Montréal', 'Toronto', 'Vancouver'],
    'United Kingdom': ['Chichester', 'Colchester', 'Derry-Londonderry', 'Edinburgh', 'London'],
    'Germany': ['Berlin', 'Cologne', 'Delme', 'Dornbirn', 'Düsseldorf', 'Frankfurt', 'Hamburg', 'Munich'],
    'France': ['Paris', 'Lyon', 'Marseille'],
    'Italy': ['Milan', 'Rome', 'Venice', 'Turin'],
    'Spain': ['Barcelona', 'Madrid'],
    'Netherlands': ['Amsterdam', 'Rotterdam'],
    'Belgium': ['Brussels', 'Antwerp'],
    'Switzerland': ['Basel', 'Zurich', 'Geneva'],
    'Austria': ['Vienna'],
    'Denmark': ['Copenhagen'],
    'Ireland': ['Dublin'],
    'New Zealand': ['Dunedin'],
    'Brazil': ['Curitiba', 'São Paulo', 'Rio de Janeiro'],
    'Senegal': ['Dakar'],
    'Qatar': ['Doha'],
    'UAE': ['Dubai'],
    'Sri Lanka': ['Colombo'],
    'Luxembourg': ['Esch'],
    'Japan': ['Tokyo'],
    'South Korea': ['Seoul'],
    'China': ['Beijing', 'Shanghai', 'Hong Kong'],
    'Australia': ['Sydney', 'Melbourne']
  };
  
  // Neighborhoods/districts for major cities (from Artforum API)
  const ARTFORUM_DISTRICTS_BY_CITY = {
    'New York': ['All', 'Chelsea', 'Downtown', 'Midtown', 'Uptown', 'Above 50th (East)', 'Above 50th (West)', 'Brooklyn', 'Boroughs'],
    'Los Angeles': ['All', 'Downtown', 'Hollywood', 'West Hollywood', 'Mid-City', 'Culver City', 'Santa Monica', 'Venice'],
    'Chicago': ['All', 'River North', 'West Loop', 'Pilsen', 'Wicker Park', 'South Loop', 'Hyde Park'],
    'London': ['All', 'Central', 'East', 'West', 'South'],
    'Berlin': ['All', 'Mitte', 'Kreuzberg', 'Charlottenburg', 'Prenzlauer Berg'],
    'Paris': ['All', 'Marais', 'Saint-Germain', 'Montmartre', 'Bastille'],
  };
  
  // Get sorted list of countries
  const ARTFORUM_COUNTRIES = Object.keys(ARTFORUM_CITIES_BY_COUNTRY).sort();
  
  // Get cities for selected country
  const getArtforumCities = () => {
    return ARTFORUM_CITIES_BY_COUNTRY[artforumCountry] || [];
  };
  
  // Get districts for selected city (if available)
  const getArtforumDistricts = () => {
    return ARTFORUM_DISTRICTS_BY_CITY[artforumCity] || null;
  };
  
  const ARTFORUM_CATEGORIES = ['all', 'galleries', 'museums', 'openings', 'closings', 'special events', 'art fairs'];

  // Story creation form state
  const [storyForm, setStoryForm] = useState({
    title: '',
    story_text: '',
    inspiration_text: '',
    process_description: '',
    story_type: 'inspiration',
    artist_uuid: '',
    vc_id: '',
    event_id: '',
    featured: false,
    performed_at_event: false,
    performance_order: null
  });

  // Event creation form state
  const [eventForm, setEventForm] = useState({
    name: '',
    description: '',
    event_start: '',
    event_end: '',
    gallery_id: '',
    event_type: 'show',
    cover_image: ''
  });

  // Load events data
  useEffect(() => {
    loadEventsData();
  }, []);

  const loadEventsData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [eventsData, analyticsData, usersData, artworksData, galleriesData] = await Promise.all([
        getEventsOverview(),
        getEventAnalytics(),
        getUsersForStoryCreation(),
        getArtworksForStoryCreation(),
        getGalleries()
      ]);

      setEvents(eventsData);
      setAnalytics(analyticsData);
      setUsers(usersData);
      setArtworks(artworksData);
      setGalleries(galleriesData);

    } catch (err) {
      console.error('Error loading events data:', err);
      setError(err.message || 'Failed to load events data');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get icon component
  const getIconComponent = (iconName) => {
    const iconMap = {
      'heart': Heart,
      'user-plus': UserPlus,
      'calendar': Calendar,
      'clock': Clock,
      'folder': Folder,
      'message-circle': MessageCircle,
      'star': Star,
      'book-open': BookOpen,
      'users': Users,
      'trending-up': TrendingUp,
      'activity': Activity,
      'award': Award
    };
    return iconMap[iconName] || Calendar;
  };

  const handleCreateStory = async () => {
    if (!storyForm.title || !storyForm.artist_uuid || !storyForm.vc_id) {
      alert('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      await createQuickStory(storyForm);
      setShowCreateModal(false);
      setStoryForm({
        title: '',
        story_text: '',
        inspiration_text: '',
        process_description: '',
        story_type: 'inspiration',
        artist_uuid: '',
        vc_id: '',
        event_id: '',
        featured: false,
        performed_at_event: false,
        performance_order: null
      });
      loadEventsData(); // Refresh data
    } catch (error) {
      console.error('Error creating story:', error);
      alert('Failed to create story');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!eventForm.name || !eventForm.event_start || !eventForm.gallery_id) {
      alert('Please fill in all required fields (Name, Start Date, and Gallery)');
      return;
    }

    setCreating(true);
    try {
      await createEvent(eventForm);
      setShowNewEventModal(false);
      setEventForm({
        name: '',
        description: '',
        event_start: '',
        event_end: '',
        gallery_id: '',
        event_type: 'show',
        cover_image: ''
      });
      setImageFile(null);
      loadEventsData(); // Refresh data
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Failed to create event');
    } finally {
      setCreating(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    try {
      setUploadingImage(true);
      const result = await uploadToCloudinary(file, 'image', 'events/covers');
      setEventForm(prev => ({
        ...prev,
        cover_image: result.url
      }));
      setImageFile(null);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleToggleFeatured = async (storyId, currentFeatured) => {
    try {
      await updateStoryFeatured(storyId, !currentFeatured);
      loadEventsData(); // Refresh data
    } catch (error) {
      console.error('Error updating story featured status:', error);
      alert('Failed to update story status');
    }
  };

  const handleDeleteStory = async (storyId) => {
    if (!confirm('Are you sure you want to delete this story?')) return;
    
    try {
      await deleteStory(storyId);
      loadEventsData(); // Refresh data
    } catch (error) {
      console.error('Error deleting story:', error);
      alert('Failed to delete story');
    }
  };

  // Scrape Chicago Gallery News events
  const handleScrapeGalleryEvents = async (dryRun = false) => {
    setScraping(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/scrape-gallery-events`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            dry_run: dryRun,
            scrape_details: false, // Set to true to scrape individual event pages (slower)
          }),
        }
      );

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to scrape events');
      }

      if (result.success) {
        let message;
        if (dryRun) {
          const matchedEvents = result.events?.filter(e => e.matched) || [];
          const unmatchedEvents = result.events?.filter(e => !e.matched) || [];
          
          message = `Dry run complete! Found ${result.events_found} events.\n\n`;
          message += `📍 GALLERY MATCHING:\n`;
          message += `• ${result.galleries_matched || 0} events matched existing galleries\n`;
          message += `• ${result.galleries_would_create || 0} new galleries would be created\n\n`;
          
          if (result.galleries_to_create?.length > 0) {
            message += `🆕 GALLERIES TO CREATE:\n`;
            message += result.galleries_to_create.map(g => `• ${g.name}`).join('\n');
            message += '\n\n';
          }
          
          message += `✅ MATCHED EVENTS (${matchedEvents.length}):\n`;
          message += matchedEvents.map(e => `• ${e.name} → Gallery #${e.gallery_id}`).join('\n') || '(none)';
          message += '\n\n';
          
          message += `⚠️ UNMATCHED EVENTS (${unmatchedEvents.length}):\n`;
          message += unmatchedEvents.map(e => `• ${e.name} (${e.venue_name || 'no venue'})`).join('\n') || '(none)';
        } else {
          message = `Scrape complete!\n\n`;
          message += `Found: ${result.events_found} events\n`;
          message += `Inserted: ${result.events_inserted}\n`;
          message += `Updated: ${result.events_updated}\n`;
          message += `Skipped: ${result.events_skipped || 0}\n\n`;
          message += `Galleries matched: ${result.galleries_matched || 0}\n`;
          message += `Galleries created: ${result.galleries_created || 0}`;
          if (result.errors?.length) {
            message += `\n\nErrors:\n${result.errors.join('\n')}`;
          }
        }
        
        alert(message);
        
        if (!dryRun) {
          loadEventsData(); // Refresh the events list
        }
      } else {
        throw new Error(result.error || 'Unknown error');
      }
    } catch (error) {
      console.error('Error scraping gallery events:', error);
      alert(`Failed to scrape events: ${error.message}`);
    } finally {
      setScraping(false);
    }
  };

  // Scrape Artforum Art Guide
  const handleScrapeArtforum = async (dryRun = false) => {
    setScrapingArtforum(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/scrape-artforum-guide`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            city: artforumCity,
            category: artforumCategory,
            district: artforumDistrict,
            dryRun: dryRun,
            includeExhibitions: true,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to scrape Artforum');
      }

      let message;
      if (result.dryRun) {
        const { summary, galleriesMatched, galleriesWouldCreate, exhibitions } = result;
        
        const districtLabel = result.district ? ` (${result.district})` : '';
        message = `🎨 ARTFORUM PREVIEW: ${result.city}${districtLabel} - ${result.category}\n\n`;
        message += `📊 SUMMARY:\n`;
        message += `• Total galleries scraped: ${summary.totalGalleriesScraped}\n`;
        message += `• Matched existing: ${summary.galleriesMatched}\n`;
        message += `• Would create: ${summary.galleriesWouldCreate}\n`;
        message += `• Exhibitions found: ${summary.exhibitionsFound}\n\n`;
        
        if (galleriesMatched?.length > 0) {
          message += `✅ MATCHED GALLERIES (${galleriesMatched.length}):\n`;
          message += galleriesMatched.slice(0, 10).map(g => `• ${g.name} → #${g.gallery_id}`).join('\n');
          if (galleriesMatched.length > 10) message += `\n  ... and ${galleriesMatched.length - 10} more`;
          message += '\n\n';
        }
        
        if (galleriesWouldCreate?.length > 0) {
          message += `🆕 GALLERIES TO CREATE (${galleriesWouldCreate.length}):\n`;
          message += galleriesWouldCreate.slice(0, 10).map(g => `• ${g.name}`).join('\n');
          if (galleriesWouldCreate.length > 10) message += `\n  ... and ${galleriesWouldCreate.length - 10} more`;
          message += '\n\n';
        }
        
        if (exhibitions?.length > 0) {
          message += `🖼️ EXHIBITIONS (${exhibitions.length}):\n`;
          message += exhibitions.slice(0, 10).map(e => 
            `• "${e.title}" at ${e.gallery}${e.dates ? ` (${e.dates})` : ''}`
          ).join('\n');
          if (exhibitions.length > 10) message += `\n  ... and ${exhibitions.length - 10} more`;
        }
      } else {
        message = `🎨 ARTFORUM IMPORT COMPLETE: ${result.city}\n\n`;
        message += `Galleries matched: ${result.results.galleriesMatched}\n`;
        message += `Galleries created: ${result.results.galleriesCreated}\n`;
        message += `Galleries skipped: ${result.results.galleriesSkipped}\n`;
        message += `Exhibitions created: ${result.results.exhibitionsCreated}\n`;
        message += `Exhibitions skipped: ${result.results.exhibitionsSkipped}`;
      }

      alert(message);

      if (!dryRun) {
        loadEventsData();
      }
    } catch (error) {
      console.error('Error scraping Artforum:', error);
      alert(`Failed to scrape Artforum: ${error.message}`);
    } finally {
      setScrapingArtforum(false);
    }
  };

  const handleAddCollection = async (eventId) => {
    try {
      // Create a new collection for this event
      const { data: collection, error } = await supabase
        .from('collections')
        .insert({
          name: `Collection for Event ${eventId}`,
          description: 'Auto-generated collection for event',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select('collection_id')
        .single();

      if (error) throw error;

      // Update the event to link to the new collection
      await supabase
        .from('events')
        .update({ collection_id: collection.collection_id })
        .eq('id', eventId);

      // Refresh data
      loadEventsData();
      
      // Open the collection in a new tab
      navigate(`/admin/collections/${collection.collection_id}`);
      
    } catch (error) {
      console.error('Error creating collection:', error);
      alert('Failed to create collection');
    }
  };

  // Filter and sort events
  const filteredAndSortedEvents = (() => {
    // First filter the events
    const filtered = events?.filter(event => {
      const location = event.gallery?.address || '';
      const matchesSearch = event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           location.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || event.status === statusFilter;
      // Show all events, let the status filter control visibility
      return matchesSearch && matchesStatus;
    }) || [];

    // Then sort the filtered events
    return filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'event_start':
          aValue = new Date(a.event_start);
          bValue = new Date(b.event_start);
          break;
        case 'created_at':
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case 'updated_at':
          aValue = new Date(a.updated_at);
          bValue = new Date(b.updated_at);
          break;
        case 'gallery':
          aValue = (a.gallery?.name || '').toLowerCase();
          bValue = (b.gallery?.name || '').toLowerCase();
          break;
        case 'story_count':
          aValue = a.story_count || 0;
          bValue = b.story_count || 0;
          break;
        case 'attendee_count':
          aValue = a.attendee_count || 0;
          bValue = b.attendee_count || 0;
          break;
        default:
          aValue = a.created_at;
          bValue = b.created_at;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  })();

  if (loading) {
    return (
      <div className="events-admin">
        <div className="events-admin-container">
          <div className="events-loading">
            <RefreshCw className="events-empty-icon spinning" color="#6c757d" />
            <h3 className="events-empty-title">Loading Events...</h3>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="events-admin">
        <div className="events-admin-container">
          <div className="events-loading">
            <X className="events-empty-icon" />
            <h3 className="events-empty-title">Error Loading Data</h3>
            <p className="events-empty-description">{error}</p>
            <button
              onClick={loadEventsData}
              className="events-btn events-btn-primary"
            >
              <RefreshCw className="events-btn-icon" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="events-admin">
      <div className="events-admin-container">
        {/* Header */}
        <div className="events-admin-header">
          <div className="events-header-content">
            <h1 className="events-admin-title">
              <Calendar className="events-tab-icon" color="#007bff" />
              Events & Exhibitions
            </h1>
            <p className="events-admin-subtitle">
              Manage events, stories, and exhibition analytics across the platform
            </p>
          </div>
          <div className="events-header-actions">
            <button
              onClick={() => navigate('/curator/events/new')}
              className="events-btn events-btn-primary"
            >
              <Plus className="events-btn-icon" color="#ffffff" />
              New Event
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="events-btn events-btn-secondary"
            >
              <Plus className="events-btn-icon" color="#6c757d" />
              Quick Story
            </button>
            <button
              onClick={loadEventsData}
              className="events-btn events-btn-secondary"
              title="Refresh Events List"
              disabled={loading}
            >
              <RefreshCw className={`events-btn-icon ${loading ? 'spinning' : ''}`} color="#6c757d" />
              Refresh
            </button>
          </div>
        </div>

        {/* Data Sources Scraper Section */}
        <div className="events-scraper-section" style={{ 
          background: '#f8f9fa', 
          padding: '16px', 
          borderRadius: '8px', 
          marginBottom: '16px',
          border: '1px solid #dee2e6'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            flexWrap: 'wrap' 
          }}>
            <span style={{ fontWeight: '600', color: '#343a40', minWidth: '80px' }}>📡 Source:</span>
            <select
              value={scraperSource}
              onChange={(e) => setScraperSource(e.target.value)}
              className="events-filter-dropdown"
              style={{ minWidth: '180px', fontWeight: '500' }}
            >
              <option value="cgn">🗞️ Chicago Gallery News</option>
              <option value="artforum">🎨 Artforum Art Guide</option>
            </select>

            {/* CGN Options */}
            {scraperSource === 'cgn' && (
              <>
                <span style={{ color: '#6c757d', fontSize: '14px' }}>Chicago events & exhibitions</span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleScrapeGalleryEvents(true)}
                    className="events-btn events-btn-secondary"
                    title="Preview events that would be scraped (dry run)"
                    disabled={scraping}
                    style={{ color: '#343a40' }}
                  >
                    <Eye className="events-btn-icon" color="#6c757d" />
                    Preview
                  </button>
                  <button
                    onClick={() => handleScrapeGalleryEvents(false)}
                    className="events-btn events-btn-primary"
                    title="Import events from Chicago Gallery News"
                    disabled={scraping}
                  >
                    <Download className={`events-btn-icon ${scraping ? 'spinning' : ''}`} color="#ffffff" />
                    {scraping ? 'Importing...' : 'Import'}
                  </button>
                </div>
              </>
            )}

            {/* Artforum Options */}
            {scraperSource === 'artforum' && (
              <>
                <select
                  value={artforumCountry}
                  onChange={(e) => {
                    const newCountry = e.target.value;
                    setArtforumCountry(newCountry);
                    // Reset city to first city in new country
                    const cities = ARTFORUM_CITIES_BY_COUNTRY[newCountry] || [];
                    setArtforumCity(cities[0] || '');
                    setArtforumDistrict('all'); // Reset district
                  }}
                  className="events-filter-dropdown"
                  style={{ minWidth: '140px' }}
                >
                  {ARTFORUM_COUNTRIES.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
                <select
                  value={artforumCity}
                  onChange={(e) => {
                    setArtforumCity(e.target.value);
                    setArtforumDistrict('all'); // Reset district when city changes
                  }}
                  className="events-filter-dropdown"
                  style={{ minWidth: '140px' }}
                >
                  {getArtforumCities().map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
                {/* District dropdown - only show for cities that have neighborhoods */}
                {getArtforumDistricts() && (
                  <select
                    value={artforumDistrict}
                    onChange={(e) => setArtforumDistrict(e.target.value)}
                    className="events-filter-dropdown"
                    style={{ minWidth: '130px' }}
                  >
                    {getArtforumDistricts().map(district => (
                      <option key={district} value={district.toLowerCase()}>{district}</option>
                    ))}
                  </select>
                )}
                <select
                  value={artforumCategory}
                  onChange={(e) => setArtforumCategory(e.target.value)}
                  className="events-filter-dropdown"
                  style={{ minWidth: '130px' }}
                >
                  {ARTFORUM_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                  ))}
                </select>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleScrapeArtforum(true)}
                    className="events-btn events-btn-secondary"
                    title="Preview galleries and exhibitions from Artforum"
                    disabled={scrapingArtforum}
                    style={{ color: '#343a40' }}
                  >
                    <Eye className="events-btn-icon" color="#6c757d" />
                    Preview
                  </button>
                  <button
                    onClick={() => handleScrapeArtforum(false)}
                    className="events-btn events-btn-primary"
                    title="Import galleries and exhibitions from Artforum"
                    disabled={scrapingArtforum}
                  >
                    <Download className={`events-btn-icon ${scrapingArtforum ? 'spinning' : ''}`} color="#ffffff" />
                    {scrapingArtforum ? 'Importing...' : 'Import'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="events-tab-navigation">
          <ul className="events-tab-list">
            {[
              { id: 'overview', label: 'Events Overview', icon: Calendar },
              { id: 'analytics', label: 'Analytics', icon: TrendingUp }
            ].map((tab) => (
              <li key={tab.id}>
                <button
                  onClick={() => setActiveTab(tab.id)}
                  className={`events-tab-button ${activeTab === tab.id ? 'active' : ''}`}
                >
                  <tab.icon className="events-tab-icon" color={tab.id === 'overview' ? '#007bff' : '#28a745'} />
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Tab Content */}
        <div className="events-tab-content">
          {/* Events Overview Tab */}
          {activeTab === 'overview' && (
            <div>
              {/* Stats Grid */}
              <div className="events-stats-grid">
                <div className="events-stat-card">
                  <div className="events-stat-content">
                    <div className="events-stat-icon events">
                      <Calendar className="events-tab-icon" color="#007bff" />
                    </div>
                    <div className="events-stat-details">
                      <div className="events-stat-label">Total Events</div>
                      <div className="events-stat-value">{events?.length || 0}</div>
                      <div className="events-stat-change">Active exhibitions</div>
                    </div>
                  </div>
                </div>

                <div className="events-stat-card">
                  <div className="events-stat-content">
                    <div className="events-stat-icon stories">
                      <BookOpen className="events-tab-icon" color="#28a745" />
                    </div>
                    <div className="events-stat-details">
                      <div className="events-stat-label">Total Stories</div>
                      <div className="events-stat-value">
                        {events?.reduce((sum, event) => sum + event.story_count, 0) || 0}
                      </div>
                      <div className="events-stat-change">Across all events</div>
                    </div>
                  </div>
                </div>

                <div className="events-stat-card">
                  <div className="events-stat-content">
                    <div className="events-stat-icon attendees">
                      <Users2 className="events-tab-icon" />
                    </div>
                    <div className="events-stat-details">
                      <div className="events-stat-label">Total Attendees</div>
                      <div className="events-stat-value">
                        {events?.reduce((sum, event) => sum + event.attendee_count, 0) || 0}
                      </div>
                      <div className="events-stat-change">Registered participants</div>
                    </div>
                  </div>
                </div>

                <div className="events-stat-card">
                  <div className="events-stat-content">
                    <div className="events-stat-icon featured">
                      <Star className="events-tab-icon" color="#ffc107" />
                    </div>
                    <div className="events-stat-details">
                      <div className="events-stat-label">Featured Stories</div>
                      <div className="events-stat-value">
                        {events?.reduce((sum, event) => sum + event.featured_stories, 0) || 0}
                      </div>
                      <div className="events-stat-change">Highlighted content</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search and Filters */}
              <div className="events-search-filters">
                <div className="events-search-row">
                  <div className="events-search-box">
                    <Search className="events-search-icon" color="#6c757d" />
                    <input
                      type="text"
                      placeholder="Search events..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="events-search-input"
                    />
                  </div>
                </div>
                <div className="events-filters-row">
                  <div className="events-filter-select">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="events-filter-dropdown"
                    >
                      <option value="all">All Status</option>
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                      <option value="upcoming">Upcoming</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div className="events-sort-controls">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="events-filter-dropdown"
                    >
                      <option value="created_at">Recently Added</option>
                      <option value="updated_at">Updated</option>
                      <option value="event_start">Event Date</option>
                      <option value="name">Name</option>
                      <option value="gallery">Gallery</option>
                      <option value="story_count">Stories</option>
                      <option value="attendee_count">Attendees</option>
                    </select>
                    <button
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                      className="sort-order-button"
                      title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                    >
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Events List */}
              <div className="events-list-container">
                {filteredAndSortedEvents.length === 0 ? (
                  <div className="events-empty">
                    <Calendar className="events-empty-icon" />
                    <h3 className="events-empty-title">No Events Found</h3>
                    <p className="events-empty-description">
                      {searchTerm || statusFilter !== 'all' 
                        ? 'Try adjusting your search or filter criteria'
                        : 'No events have been created yet'
                      }
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="events-results-info">
                      <span className="results-count">
                        {filteredAndSortedEvents.length} event{filteredAndSortedEvents.length !== 1 ? 's' : ''}
                      </span>
                      <span className="sort-info">
                        sorted by {sortBy === 'created_at' ? 'recently added' : 
                                  sortBy === 'updated_at' ? 'recently updated' :
                                  sortBy === 'event_start' ? 'event date' :
                                  sortBy === 'name' ? 'event name' :
                                  sortBy === 'gallery' ? 'gallery name' :
                                  sortBy === 'story_count' ? 'story count' :
                                  sortBy === 'attendee_count' ? 'attendee count' : sortBy}
                        {' '}({sortOrder === 'asc' ? 'ascending' : 'descending'})
                      </span>
                    </div>
                    <div className="events-table">
                    <div className="events-table-header">
                      <div className="header-cell name">Event Name</div>
                      <div className="header-cell date">Date</div>
                      <div className="header-cell gallery">Gallery</div>
                      <div className="header-cell stats">Stories</div>
                      <div className="header-cell stats">Attendees</div>
                      <div className="header-cell status">Status</div>
                    </div>
                    {filteredAndSortedEvents.map((event) => (
                      <EventRow
                        key={event.id}
                        event={event}
                        navigate={navigate}
                      />
                    ))}
                  </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="events-analytics">
              <div className="analytics-header">
                <h2>Story Analytics</h2>
                <p>Engagement metrics and performance insights</p>
              </div>

              <div className="analytics-grid">
                <div className="analytics-card">
                  <h3>Story Performance</h3>
                  <div className="analytics-metrics">
                    <div className="metric">
                      <span className="metric-label">Total Stories</span>
                      <span className="metric-value">{analytics?.total_stories || 0}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Featured Stories</span>
                      <span className="metric-value">{analytics?.featured_stories || 0}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Total Views</span>
                      <span className="metric-value">{analytics?.total_views || 0}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Total Likes</span>
                      <span className="metric-value">{analytics?.total_likes || 0}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Total Shares</span>
                      <span className="metric-value">{analytics?.total_shares || 0}</span>
                    </div>
                  </div>
                </div>

                <div className="analytics-card">
                  <h3>Story Types</h3>
                  <div className="story-types">
                    {analytics?.story_types && Object.entries(analytics.story_types).map(([type, count]) => (
                      <div key={type} className="story-type-item">
                        <span className="type-name">{type}</span>
                        <span className="type-count">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="analytics-card">
                  <h3>Recent Stories</h3>
                  <div className="recent-stories">
                    {analytics?.recent_stories?.map((story) => (
                      <div key={story.story_id} className="recent-story-item">
                        <div className="story-info">
                          <h4>{story.title}</h4>
                          <p>by {story.artist?.nickname || 'Unknown'}</p>
                        </div>
                        <div className="story-meta">
                          <span className="story-type">{story.story_type}</span>
                          {story.featured && <Star className="featured-icon" color="#ffc107" />}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Quick Story Creation Modal */}
        {showCreateModal && createPortal(
          <QuickStoryModal
            form={storyForm}
            setForm={setStoryForm}
            users={users}
            artworks={artworks}
            events={events}
            onSubmit={handleCreateStory}
            onClose={() => setShowCreateModal(false)}
            creating={creating}
          />,
          document.body
        )}

        {/* New Event Creation Modal */}
        {showNewEventModal && createPortal(
          <NewEventModal
            form={eventForm}
            setForm={setEventForm}
            galleries={galleries}
            onSubmit={handleCreateEvent}
            onClose={() => setShowNewEventModal(false)}
            creating={creating}
            onImageUpload={handleImageUpload}
            uploadingImage={uploadingImage}
            imageFile={imageFile}
            setImageFile={setImageFile}
          />,
          document.body
        )}
        

      </div>
    </div>
  );
};

// Event Row Component (Compact List Format)
const EventRow = ({ event, navigate }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'status-active';
      case 'upcoming': return 'status-upcoming';
      case 'completed': return 'status-completed';
      case 'draft': return 'status-draft';
      case 'cancelled': return 'status-cancelled';
      default: return 'status-default';
    }
  };

  const handleRowClick = () => {
    navigate(`/curator/events/${event.id}`);
  };

  return (
    <div className="event-row" onClick={handleRowClick}>
      <div className="row-cell name">
        <div className="event-name">{event.name}</div>
        <div className="event-type">{event.event_type || 'show'}</div>
      </div>
      <div className="row-cell date">
        <div className="event-date">{formatDate(event.event_start)}</div>
        {event.event_end && (
          <div className="event-end-date">to {formatDate(event.event_end)}</div>
        )}
      </div>
      <div className="row-cell gallery">
        <div className="gallery-name">{event.gallery?.name || 'Unknown Gallery'}</div>
        <div className="gallery-address">{event.gallery?.address}</div>
      </div>
      <div className="row-cell stats">
        <span className="stat-number">{event.story_count || 0}</span>
      </div>
      <div className="row-cell stats">
        <span className="stat-number">{event.attendee_count || 0}</span>
      </div>
      <div className="row-cell status">
        <span className={`event-status ${getStatusColor(event.status)}`}>
          {event.status}
        </span>
      </div>
    </div>
  );
};

// Event Card Component (Keep for backwards compatibility if needed)
const EventCard = ({ event, onToggleFeatured, onDeleteStory, navigate }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'status-active';
      case 'upcoming': return 'status-upcoming';
      case 'completed': return 'status-completed';
      default: return 'status-default';
    }
  };

  return (
    <div className="event-card">
      <div className="event-header">
        <div className="event-title">
          <h3>{event.name}</h3>
          <span className={`event-status ${getStatusColor(event.status)}`}>
            {event.status}
          </span>
        </div>
        <div className="event-actions">
          <button 
            className="event-action-btn"
            onClick={() => navigate(`/admin/events/${event.id}`)}
            title="View Event Details"
          >
            <Eye className="action-icon" />
          </button>
          <button className="event-action-btn">
            <Edit className="action-icon" />
          </button>
          {event.collection_id ? (
            <button 
              className="event-action-btn collection-btn"
              onClick={() => navigate(`/admin/collections/${event.collection_id}`)}
              title="View Collection"
            >
              <BookOpen className="action-icon" />
            </button>
          ) : (
            <button 
              className="event-action-btn add-collection-btn"
              onClick={() => handleAddCollection(event.id)}
              title="Add Collection"
            >
              <Plus className="action-icon" />
            </button>
          )}
        </div>
      </div>

      <div className="event-details">
        <div className="event-meta">
          <div className="event-date">
            <CalendarDays className="meta-icon" />
            <span>{formatDate(event.event_start)}</span>
          </div>
          {event.gallery?.address && (
            <div className="event-location">
              <MapPin className="meta-icon" />
              <span>{event.gallery.address}</span>
            </div>
          )}
        </div>

        <div className="event-stats">
          <div className="stat-item">
            <BookOpen className="stat-icon" />
            <span className="stat-value">{event.story_count}</span>
            <span className="stat-label">Stories</span>
          </div>
          <div className="stat-item">
            <Users2 className="stat-icon" />
            <span className="stat-value">{event.attendee_count}</span>
            <span className="stat-label">Attendees</span>
          </div>
          <div className="stat-item">
            <CheckCircle className="stat-icon" />
            <span className="stat-value">{event.checked_in_count}</span>
            <span className="stat-label">Checked In</span>
          </div>
          <div className="stat-item">
            <Star className="stat-icon" />
            <span className="stat-value">{event.featured_stories}</span>
            <span className="stat-label">Featured</span>
          </div>
        </div>

        {event.stories && event.stories.length > 0 && (
          <div className="event-stories">
            <h4>Recent Stories</h4>
            <div className="stories-list">
              {event.stories.slice(0, 3).map((story) => (
                <div key={story.story_id} className="story-item">
                  <div className="story-info">
                    <span className="story-title">{story.title}</span>
                    <span className="story-type">{story.story_type}</span>
                  </div>
                  <div className="story-actions">
                    <button
                      onClick={() => onToggleFeatured(story.story_id, story.featured)}
                      className={`story-action-btn ${story.featured ? 'featured' : ''}`}
                    >
                      <Star className="action-icon" />
                    </button>
                    <button
                      onClick={() => onDeleteStory(story.story_id)}
                      className="story-action-btn delete"
                    >
                      <Trash2 className="action-icon" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Quick Story Creation Modal
const QuickStoryModal = ({ form, setForm, users, artworks, events, onSubmit, onClose, creating }) => {
  const [filteredUsers, setFilteredUsers] = useState(users);
  const [filteredArtworks, setFilteredArtworks] = useState(artworks);
  const [selectedCollection, setSelectedCollection] = useState(null);

  // Filter users and artworks when collection changes
  useEffect(() => {
    if (form.event_id) {
      const event = events.find(e => e.id === parseInt(form.event_id));
      if (event?.collection_id) {
        setSelectedCollection(event.collection_id);
        // Filter users and artworks by collection (this would need API calls)
        // For now, we'll show all users and artworks
        setFilteredUsers(users);
        setFilteredArtworks(artworks);
      } else {
        setSelectedCollection(null);
        setFilteredUsers(users);
        setFilteredArtworks(artworks);
      }
    } else {
      setSelectedCollection(null);
      setFilteredUsers(users);
      setFilteredArtworks(artworks);
    }
  }, [form.event_id, events, users, artworks]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Quick Story</h2>
          <button onClick={onClose} className="close-btn">
            <X className="close-icon" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>Story Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Enter story title"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Artist *</label>
              <select
                value={form.artist_uuid}
                onChange={(e) => setForm({ ...form, artist_uuid: e.target.value })}
                required
              >
                <option value="">Select Artist</option>
                {filteredUsers.map((user) => (
                  <option key={user.uuid} value={user.uuid}>
                    {user.nickname || `${user.first_name} ${user.last_name}`}
                  </option>
                ))}
              </select>
              {selectedCollection && (
                <small className="form-hint">Filtered by collection artists</small>
              )}
            </div>

            <div className="form-group">
              <label>Artwork *</label>
              <select
                value={form.vc_id}
                onChange={(e) => setForm({ ...form, vc_id: e.target.value })}
                required
              >
                <option value="">Select Artwork</option>
                {filteredArtworks.map((artwork) => (
                  <option key={artwork.vc_id} value={artwork.vc_id}>
                    {artwork.title}
                  </option>
                ))}
              </select>
              {selectedCollection && (
                <small className="form-hint">Filtered by collection artworks</small>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Event</label>
              <select
                value={form.event_id}
                onChange={(e) => setForm({ ...form, event_id: e.target.value })}
              >
                <option value="">No Event</option>
                {events.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Story Type</label>
              <select
                value={form.story_type}
                onChange={(e) => setForm({ ...form, story_type: e.target.value })}
              >
                <option value="inspiration">Inspiration</option>
                <option value="process">Process</option>
                <option value="reflection">Reflection</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Story Text</label>
            <textarea
              value={form.story_text}
              onChange={(e) => setForm({ ...form, story_text: e.target.value })}
              placeholder="Tell the story behind this artwork..."
              rows={4}
            />
          </div>

          <div className="form-group">
            <label>Inspiration Text</label>
            <textarea
              value={form.inspiration_text}
              onChange={(e) => setForm({ ...form, inspiration_text: e.target.value })}
              placeholder="What inspired this piece?"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Process Description</label>
            <textarea
              value={form.process_description}
              onChange={(e) => setForm({ ...form, process_description: e.target.value })}
              placeholder="Describe the creative process..."
              rows={3}
            />
          </div>

          <div className="form-options">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
              />
              <span>Featured Story</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.performed_at_event}
                onChange={(e) => setForm({ ...form, performed_at_event: e.target.checked })}
              />
              <span>Performed at Event</span>
            </label>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="events-btn events-btn-secondary">
              Cancel
            </button>
            <button type="submit" className="events-btn events-btn-primary" disabled={creating}>
              {creating ? 'Creating...' : 'Create Story'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// New Event Modal Component
const NewEventModal = ({ 
  form, 
  setForm, 
  galleries, 
  onSubmit, 
  onClose, 
  creating,
  onImageUpload,
  uploadingImage,
  imageFile,
  setImageFile
}) => {
  const [gallerySearch, setGallerySearch] = useState('');
  const [showGalleryDropdown, setShowGalleryDropdown] = useState(false);
  const [selectedGallery, setSelectedGallery] = useState(null);

  // Filter galleries based on search
  const filteredGalleries = galleries?.filter(gallery =>
    (gallery.name?.toLowerCase() || '').includes(gallerySearch.toLowerCase()) ||
    (gallery.address?.toLowerCase() || '').includes(gallerySearch.toLowerCase())
  ) || [];

  // Find selected gallery for display
  React.useEffect(() => {
    if (form.gallery_id && galleries) {
      const selected = galleries.find(g => g.gallery_id === form.gallery_id);
      setSelectedGallery(selected);
      setGallerySearch(selected ? `${selected.name || ''} - ${selected.address || ''}` : '');
    }
  }, [form.gallery_id, galleries]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      onImageUpload(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setForm({ ...form, cover_image: '' });
  };

  const handleGallerySelect = (gallery) => {
    setForm({ ...form, gallery_id: gallery.gallery_id });
    setSelectedGallery(gallery);
    setGallerySearch(`${gallery.name || ''} - ${gallery.address || ''}`);
    setShowGalleryDropdown(false);
  };

  const handleGallerySearchChange = (e) => {
    const value = e.target.value;
    setGallerySearch(value);
    setShowGalleryDropdown(true);
    
    // Clear selection if user is typing
    if (value !== (selectedGallery ? `${selectedGallery.name || ''} - ${selectedGallery.address || ''}` : '')) {
      setForm({ ...form, gallery_id: '' });
      setSelectedGallery(null);
    }
  };

  const clearGallerySelection = () => {
    setGallerySearch('');
    setForm({ ...form, gallery_id: '' });
    setSelectedGallery(null);
    setShowGalleryDropdown(false);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.searchable-select')) {
        setShowGalleryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="modal-overlay">
      <div className="modal-content events-modal">
        <div className="modal-header">
          <h2>Create New Event</h2>
          <button onClick={onClose} className="close-button">
            <X size={24} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Event Name*</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Enter event name..."
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Short description of the event..."
              rows={3}
            />
          </div>

          {/* Cover Image Upload */}
          <div className="form-group">
            <label>Cover Image</label>
            <div className="image-upload-container">
              {!form.cover_image && !imageFile && (
                <div className="upload-area">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={uploadingImage}
                    style={{ display: 'none' }}
                    id="cover-image-input"
                  />
                  <label htmlFor="cover-image-input" className="upload-label">
                    <div className="upload-placeholder">
                      <Calendar size={32} color="#6c757d" />
                      <p>Click to upload cover image</p>
                      <span>Recommended: 800x800px (Square)</span>
                    </div>
                  </label>
                </div>
              )}
              
              {uploadingImage && (
                <div className="uploading-indicator">
                  <div className="spinner"></div>
                  Uploading...
                </div>
              )}
              
              {form.cover_image && (
                <div className="uploaded-image">
                  <img src={form.cover_image} alt="Cover" />
                  <button type="button" onClick={removeImage} className="remove-image-btn">
                    <X size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Start Date*</label>
            <input
              type="datetime-local"
              value={form.event_start}
              onChange={(e) => setForm({ ...form, event_start: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>End Date</label>
            <input
              type="datetime-local"
              value={form.event_end}
              onChange={(e) => setForm({ ...form, event_end: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label>Gallery*</label>
            <div className="searchable-select">
              <div className="input-with-clear">
                <input
                  type="text"
                  value={gallerySearch}
                  onChange={handleGallerySearchChange}
                  onFocus={() => setShowGalleryDropdown(true)}
                  placeholder="Search galleries..."
                  required
                  autoComplete="off"
                />
                {gallerySearch && (
                  <button
                    type="button"
                    className="clear-button"
                    onClick={clearGallerySelection}
                    title="Clear selection"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              {showGalleryDropdown && filteredGalleries.length > 0 && (
                <div className="dropdown-list">
                  {filteredGalleries.map((gallery) => (
                    <div
                      key={gallery.gallery_id}
                      className="dropdown-item"
                      onClick={() => handleGallerySelect(gallery)}
                    >
                      <div className="gallery-name">{gallery.name || 'Unnamed Gallery'}</div>
                      <div className="gallery-address">{gallery.address || 'No address provided'}</div>
                    </div>
                  ))}
                </div>
              )}
              {showGalleryDropdown && gallerySearch && filteredGalleries.length === 0 && (
                <div className="dropdown-list">
                  <div className="dropdown-item no-results">
                    No approved galleries found
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Event Type</label>
            <select
              value={form.event_type}
              onChange={(e) => setForm({ ...form, event_type: e.target.value })}
            >
              <option value="show">Show</option>
              <option value="exhibition">Exhibition</option>
              <option value="workshop">Workshop</option>
              <option value="opening">Opening</option>
              <option value="artist_talk">Artist Talk</option>
            </select>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="events-btn events-btn-secondary">
              Cancel
            </button>
            <button type="submit" className="events-btn events-btn-primary" disabled={creating}>
              {creating ? 'Creating...' : 'Create Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventsAdmin;
